import { motion } from "framer-motion";
import TextScramble from "@/components/TextScramble";
import AnimatedBorder from "@/components/AnimatedBorder";
import TipsWidget from "@/components/TipsWidget";

const manifesto = [
  "This dossier isn't a résumé — it's a callout.",
  "Every project is a smoke thrown at a stubborn problem.",
  "Every commit is a headshot toward something shipped.",
];

const skills = [
  { name: "React", level: 92 },
  { name: "TypeScript", level: 88 },
  { name: "Next.js", level: 85 },
  { name: "Three.js", level: 78 },
  { name: "Tailwind", level: 95 },
  { name: "Node.js", level: 80 },
  { name: "Python", level: 75 },
  { name: "Cloudflare", level: 82 },
];

const pcSpecs = [
  { label: "CPU", value: "Ryzen 5 4500" },
  { label: "GPU", value: "GTX 1070 FE" },
  { label: "RAM", value: "2x8 GB DDR4" },
  { label: "MB", value: "ASUS B450M-K II" },
  { label: "CASE", value: "Lian Li O11D Mini V2" },
];

export default function About() {
  return (
    <section id="about" className="relative px-6 md:px-12 py-24 md:py-32 section-glow section-scan" data-testid="about-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9 }}
        className="relative z-10 max-w-6xl"
      >
        <div className="flex items-baseline gap-6 mb-6">
          <span className="font-mono text-sm text-[#F47B20]">CH.01</span>
          <span className="h-px flex-1 bg-gradient-to-r from-[#F47B20]/30 to-white/5 section-line" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
            ABOUT / INTEL
          </span>
        </div>

        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-7xl lg:text-8xl uppercase leading-[0.9]">
          Codename <br />
          <TextScramble text="Aeoneuh" className="text-stroke-orange inline-block" />
        </h2>

        <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5 border-l border-[#F47B20] pl-5 md:pl-6 space-y-6">
            {manifesto.map((line, i) => (
              <motion.p
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.9, delay: 0.15 * i, ease: [0.65, 0, 0.35, 1] }}
                className="font-display font-light text-xl md:text-2xl text-white leading-snug"
              >
                <span className="text-[#F47B20] font-mono text-xs mr-3 align-top">
                  0{i + 1}
                </span>
                {line}
              </motion.p>
            ))}
          </div>

          <div className="md:col-span-6 md:col-start-7 space-y-5">
            {/* Operative file card */}
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="border border-white/10 bg-[#0d0d0f] p-6 clip-tactical-sm noise-overlay glow-border"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">
                [ FILE / OPERATIVE ]
              </div>
              <dl className="grid grid-cols-2 gap-y-3 gap-x-6 font-mono text-sm">
                <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">Alias</dt>
                <dd className="text-white">Aeoneuh</dd>
                <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">Class</dt>
                <dd className="text-white">Dev-Op / Entry</dd>
                <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">Weapons</dt>
                <dd className="text-white">Keyboard, AK-47</dd>
                <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">Ops Since</dt>
                <dd className="text-white">2015</dd>
                <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">Sector</dt>
                <dd className="text-white">Web / CS2</dd>
                <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">Status</dt>
                <dd className="text-green-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-green-400 rounded-full pulse-dot" />
                  ACTIVE
                </dd>
              </dl>
            </motion.div>

            {/* Bio text */}
            <div className="font-mono text-sm text-white/60 leading-relaxed">
              Half operative, half front-end sniper. Aeoneuh builds interfaces the
              way pros pop headshots — deliberately, quickly, with a mechanical
              obsession for the details other people overlook.
            </div>

            {/* Skills */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                [ ARSENAL.SKILLS ]
              </div>
              <div className="space-y-2.5">
                {skills.map((s) => (
                  <div key={s.name} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/50 group-hover:text-white/70 transition-colors">
                        {s.name}
                      </span>
                      <span className="font-mono text-[10px] text-[#F47B20]">
                        {s.level}%
                      </span>
                    </div>
                    <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${s.level}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.2, delay: 0.1, ease: [0.65, 0, 0.35, 1] }}
                        className="h-full bg-gradient-to-r from-[#F47B20] to-[#D9C850] rounded-full"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* PC Specs */}
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">
                [ RIG.CONFIG ]
              </div>
              <div className="border border-white/10 bg-[#0d0d0f] p-4 clip-tactical-sm noise-overlay">
                <dl className="grid grid-cols-2 gap-x-6 gap-y-2.5 font-mono text-sm">
                  {pcSpecs.map((s) => (
                    <div key={s.label} className="contents">
                      <dt className="text-white/40 uppercase text-[10px] tracking-[0.2em]">{s.label}</dt>
                      <dd className="text-white text-[13px]">{s.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}
